import { useState, useEffect } from 'react';
import { useOutletContext, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Plus, Search, Edit2, Trash2, UserPlus, ChevronRight, Eye, Sparkles,
  MoreVertical, Archive, CheckCircle, CreditCard, AlertCircle, Clock, DollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAM_PRICE = 9.99; // USD per month

const ClubTeams = () => {
  const { club } = useOutletContext();
  const { clubId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [activatingTeam, setActivatingTeam] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    max_athletes: 30
  });

  useEffect(() => {
    fetchTeams();
    fetchSubscriptionInfo();
    
    // Check for payment return
    const paymentStatus = searchParams.get('payment');
    const teamId = searchParams.get('team_id');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId && teamId) {
      pollPaymentStatus(teamId, sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (teamId, sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please check your email for confirmation.');
      return;
    }
    
    const token = localStorage.getItem('club_token');
    try {
      const response = await axios.get(
        `${API}/clubs/teams/${teamId}/checkout/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Team is now active.');
        fetchTeams();
        fetchSubscriptionInfo();
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(teamId, sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please refresh the page.');
    }
  };

  const fetchSubscriptionInfo = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const response = await axios.get(`${API}/clubs/subscription-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
    }
  };

  useEffect(() => {
    fetchTeams();
    fetchSubscriptionInfo();
    
    // Check for payment return
    const paymentStatus = searchParams.get('payment');
    const teamId = searchParams.get('team_id');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId && teamId) {
      pollPaymentStatus(teamId, sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams]);

  const fetchTeams = async () => {
    const token = localStorage.getItem('club_token');
    try {
      const response = await axios.get(`${API}/clubs/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      setTeams([]);
    }
    setLoading(false);
  };

  const handleActivateTeam = async (team) => {
    setActivatingTeam(team.id);
    const token = localStorage.getItem('club_token');
    try {
      // Founding Beta: instant activation, no payment required
      const response = await axios.post(
        `${API}/clubs/teams/${team.id}/mock-payment`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Team activated — Founding Beta access');
      fetchTeams();
      fetchSubscriptionInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate team');
    } finally {
      setActivatingTeam(null);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('club_token');
    try {
      const created = await axios.post(`${API}/clubs/teams`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Founding Beta: auto-activate team immediately (no payment friction)
      const newTeam = created?.data;
      if (newTeam?.id && newTeam.status !== 'active') {
        try {
          await axios.post(
            `${API}/clubs/teams/${newTeam.id}/mock-payment`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (e) { /* non-blocking */ }
      }
      toast.success('Team created — Founding Beta access activated');
      setShowCreateModal(false);
      setFormData({ name: '', category: '', description: '', max_athletes: 30 });
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('club_token');
    try {
      await axios.put(`${API}/clubs/teams/${editingTeam.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Team updated successfully!');
      setEditingTeam(null);
      fetchTeams();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update team');
    }
  };

  const handleArchive = async (teamId) => {
    if (!confirm('Are you sure you want to archive this team?')) return;
    const token = localStorage.getItem('club_token');
    try {
      await axios.put(`${API}/clubs/teams/${teamId}`, { status: 'archived' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Team archived');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to archive team');
    }
  };

  const filteredTeams = teams.filter(team => 
    team.name.toLowerCase().includes(search.toLowerCase()) ||
    team.category.toLowerCase().includes(search.toLowerCase())
  );

  const openEdit = (team) => {
    setFormData({
      name: team.name,
      category: team.category,
      description: team.description || '',
      max_athletes: team.max_athletes
    });
    setEditingTeam(team);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingTeam(null);
    setFormData({ name: '', category: '', description: '', max_athletes: 30 });
  };

  return (
    <div className="space-y-6" data-testid="club-teams">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl uppercase">
            Teams <span className="text-emerald-400">Management</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage your soccer academy teams • <span className="text-primary">Founding Beta — included free</span>
          </p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="bg-emerald-500 text-black hover:bg-emerald-400"
        >
          <Plus size={18} className="mr-2" />
          Create Team
        </Button>
      </div>

      {/* Subscription Summary */}
      {subscriptionInfo && (
        <div className="bg-gradient-to-r from-emerald-500/10 to-transparent border border-emerald-500/30 rounded-sm p-4">
          <div className="flex flex-wrap gap-6 items-center">
            <div className="flex items-center gap-2">
              <CheckCircle className="text-emerald-400" size={18} />
              <span className="text-sm">
                <span className="font-semibold text-emerald-400">{subscriptionInfo.active_teams}</span> Active Teams
              </span>
            </div>
            {subscriptionInfo.pending_teams > 0 && (
              <div className="flex items-center gap-2">
                <Sparkles className="text-primary" size={18} />
                <span className="text-sm">
                  <span className="font-semibold text-primary">{subscriptionInfo.pending_teams}</span> Pending Activation
                </span>
              </div>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <Sparkles className="text-primary" size={14} />
              <span className="text-xs uppercase tracking-widest text-primary font-semibold">
                Founding Beta · Complimentary Access
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search teams..."
          className="bg-transparent border-border pl-10"
        />
      </div>

      {/* Teams Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto"></div>
        </div>
      ) : filteredTeams.length === 0 ? (
        <div className="text-center py-12 bg-card border border-border/40 rounded-sm">
          <Users className="mx-auto text-muted-foreground mb-4" size={48} />
          <p className="text-muted-foreground mb-4">
            {search ? 'No teams found matching your search' : 'No teams yet'}
          </p>
          {!search && (
            <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-500 text-black hover:bg-emerald-400">
              <Plus size={18} className="mr-2" />
              Create Your First Team
            </Button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeams.map((team, idx) => (
            <motion.div
              key={team.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className={`bg-card border rounded-sm p-5 ${
                team.status === 'pending_payment' 
                  ? 'border-amber-500/50' 
                  : team.status === 'inactive' || team.status === 'archived'
                  ? 'border-border/20 opacity-60' 
                  : 'border-border/40 hover:border-emerald-500/50'
              } transition-colors`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                    team.status === 'pending_payment' 
                      ? 'bg-amber-500/20' 
                      : 'bg-emerald-500/20'
                  }`}>
                    <Users className={team.status === 'pending_payment' ? 'text-amber-400' : 'text-emerald-400'} size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold">{team.name}</h3>
                    <p className="text-xs text-muted-foreground">{team.category}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-sm flex items-center gap-1 ${
                  team.status === 'active' 
                    ? 'bg-emerald-500/20 text-emerald-400' 
                    : team.status === 'pending_payment'
                    ? 'bg-primary/15 text-primary'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {team.status === 'pending_payment' ? (
                    <>
                      <Sparkles size={12} />
                      Pending Activation
                    </>
                  ) : team.status === 'active' ? (
                    <>
                      <CheckCircle size={12} />
                      Active
                    </>
                  ) : (
                    team.status
                  )}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                <div className="bg-card/50 border border-border/30 rounded-sm p-2 text-center">
                  <p className="font-semibold">{team.athletes_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Players</p>
                </div>
                <div className="bg-card/50 border border-border/30 rounded-sm p-2 text-center">
                  <p className="font-semibold">{team.coaches_count || 0}</p>
                  <p className="text-xs text-muted-foreground">Coaches</p>
                </div>
              </div>

              {team.description && (
                <p className="text-xs text-muted-foreground mb-4 line-clamp-2">{team.description}</p>
              )}

              {/* Action Buttons */}
              {team.status === 'pending_payment' ? (
                <div className="space-y-2">
                  <div className="bg-primary/5 border border-primary/20 rounded-sm p-2.5 flex items-center gap-2">
                    <Sparkles size={12} className="text-primary shrink-0" />
                    <p className="text-[11px] text-primary leading-snug">
                      Founding Beta — activate this team free
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="w-full bg-primary text-black hover:bg-primary/90 font-bold uppercase tracking-wider text-xs"
                    onClick={() => handleActivateTeam(team)}
                    disabled={activatingTeam === team.id}
                    data-testid={`team-activate-${team.id}`}
                  >
                    {activatingTeam === team.id ? 'Activating...' : 'Activate Free'}
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-primary/50 text-primary hover:bg-primary/10"
                    onClick={() => navigate(`/club/${clubId}/teams/${team.id}`)}
                    data-testid={`team-view-overview-${team.id}`}
                  >
                    <Eye size={14} className="mr-1" />
                    View Overview
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                    onClick={() => openEdit(team)}
                    data-testid={`team-edit-${team.id}`}
                  >
                    <Edit2 size={14} />
                  </Button>
                  {team.status === 'active' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-border/50 text-muted-foreground hover:text-red-400 hover:border-red-500/50"
                      onClick={() => handleArchive(team.id)}
                    >
                      <Archive size={14} />
                    </Button>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTeam) && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card border border-border/40 rounded-sm w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="font-heading text-xl uppercase mb-2">
              {editingTeam ? 'Edit Team' : 'Create New Team'}
            </h2>
            
            {!editingTeam && (
              <div className="bg-primary/5 border border-primary/20 rounded-sm p-3 mb-4">
                <p className="text-xs text-primary leading-relaxed">
                  <Sparkles className="inline mr-1.5 -mt-0.5" size={13} />
                  <strong>Founding Beta:</strong> teams are activated free during the validation phase. Future pricing: ${TEAM_PRICE}/team/month.
                </p>
              </div>
            )}
            
            <form onSubmit={editingTeam ? handleUpdate : handleCreate} className="space-y-4">
              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Team Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="bg-transparent border-border focus:border-emerald-500"
                  placeholder="e.g., U14 Elite"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Age Category *
                </label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  required
                  className="bg-transparent border-border focus:border-emerald-500"
                  placeholder="e.g., U14, U12, Senior"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Description
                </label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-transparent border-border focus:border-emerald-500"
                  placeholder="Optional team description"
                />
              </div>

              <div>
                <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">
                  Max Players
                </label>
                <Input
                  type="number"
                  value={formData.max_athletes}
                  onChange={(e) => setFormData({...formData, max_athletes: parseInt(e.target.value)})}
                  min={1}
                  max={100}
                  className="bg-transparent border-border focus:border-emerald-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={closeModal}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400"
                >
                  {editingTeam ? 'Save Changes' : 'Create Team'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ClubTeams;
